// @flow

import React from "react";
import { localize } from "redux-i18n";
import { connect } from "react-redux";
import { compose } from "redux";
import { Field, reduxForm } from "redux-form";
import {
  type StepProps,
  Form,
  FormGroup,
  Label,
  GSGrid,
  Icons,
  Button,
  Pagination,
  View,
  Text,
} from "@components";

import type { State } from "@store/reducers";
import employeeActionCreators from "@modules/employee/actionCreator";
import hierarchyActionCreators from "@modules/hierarchy/actionCreator";
import * as hierarchySelectors from "@modules/hierarchy/selectors";
import type { NotificationContact } from "../../reducer/hierarchy/types";
import { DatePickerFormWrapper, TextField } from "@components/form";
import { getReduxForm } from "@selectors";
import type { EmployeeType } from "@modules/employee/types";
import config from "@config";

type PropsFromState = $Exact<{
  employeesSearchResult: Array<Object>,
  selectEmployeesPagination: Object,
  notificationContacts: NotificationContact[],
  contactFilter: Object,
}>;

type PropsFromDispatch = $Exact<{
  setNotificationContact: typeof hierarchyActionCreators.SetNotificationContact,
  callbacksEmployeeSearch: typeof employeeActionCreators.CallbacksEmployeeSearch,
}>;

type OwnProps = $Exact<{
  clientId: number,
  selectedNotificationContact: NotificationContact,
  editable: boolean,
}>;

type Props = $Exact<{
  ...StepProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
  ...OwnProps,
}>;

type ComponentState = $Exact<{
  currentEmployeeId: ?number,
}>;

export class Employees extends React.Component<Props, ComponentState> {
  onSubmitStep = (item: EmployeeType) => () => {
    this.props.setNotificationContact({
      ...item,
      organisationalUnit: { name: item.employmentSummary.organisationalUnits[0] || "" },
      createNew: true,
    });
    this.props.handleSubmit(item, true);
  };

  get columns() {
    let cols = [
      { key: "employeeRefPrimary", name: this.props.t("EMPLOYEE_REF"), align: "left" },
      { key: "firstName", align: "left", name: this.props.t("FIRST_NAME") },
      { key: "lastName", align: "left", name: this.props.t("SURNAME") },
      {
        key: "email",
        align: "left",
        name: this.props.t("EMAIL"),
        renderCell: ({ row }: { row: any }) => {
          return (
            row?.contactDetails?.workEmail || <Text text={this.props.t("MISSING")} color="red" />
          );
        },
      },
    ];
    if (this.props.selectedNotificationContact) {
      cols.push({
        key: "department",
        name: this.props.t("DEPARTMENT"),
        renderCell: ({ row }: { row: any }) => {
          return row.department;
        },
      });
    } else {
      cols.push({
        key: "options",
        name: "",
        width: 50,
        renderCell: ({ row }: Object) =>
          this.props.editable && (
            <span onClick={this.onSubmitStep(row)} className="material-icons font-size-30 pointer">
              play_circle
            </span>
          ),
      });
    }
    return cols;
  }

  get data() {
    return this.props.selectedNotificationContact
      ? [
          {
            ...this.props.selectedNotificationContact,
            ...this.props.selectedNotificationContact.recipient,
            department: this.props.selectedNotificationContact.organisationalUnit.name,
          },
        ]
      : this.props.employeesSearchResult.map((userData: EmployeeType) => {
          return userData;
        });
  }

  handleSearch = (e?: SyntheticEvent<HTMLButtonElement>) => {
    if (e && e.preventDefault) e.preventDefault();

    this.props.callbacksEmployeeSearch({
      clientId: this.props.clientId,
      data: { ...this.props.contactFilter.values, is_active: true }, // provide filter is_active to limit the results
      limit: config.shortResultsPerPage,
      page: !isNaN(e) ? parseInt(e) : 1,
    });
  };

  render() {
    return (
      <View>
        <View justifyContent="center">
          {!this.props.selectedNotificationContact && this.props.editable && (
            <Form
              onSubmit={this.handleSearch}
              className="d-flex flex-columns justify-content-center"
            >
              <FormGroup className={"mr-2"}>
                <Label for="lastName">{this.props.t("SURNAME")}</Label>
                <Field id="lastName" name="lastName" component={TextField} type="text" />
              </FormGroup>
              <FormGroup className={"mx-2"}>
                <Label for="employeeRefPrimary">{this.props.t("EMPLOYEE_REF")}</Label>
                <Field
                  id="employeeRefPrimary"
                  name="employeeRefPrimary"
                  component={TextField}
                  type="text"
                />
              </FormGroup>
              <FormGroup className={"mx-2"}>
                <Label for="dateOfBirth">{this.props.t("DATE_OF_BIRTH")}</Label>
                <Field
                  name="dateOfBirth"
                  customProps={{ dateFormat: config.dateFormatBE, id: "dateOfBirth" }}
                  component={DatePickerFormWrapper}
                />
              </FormGroup>
              <FormGroup className={"ml-2 pt-3"}>
                <Button className={"mt-3"} materialIcon="replay_circle_filled">
                  {this.props.t("SUBMIT")}
                </Button>
              </FormGroup>
            </Form>
          )}
        </View>
        <GSGrid className="my-3" rows={this.data} columns={this.columns} />
        {!this.props.selectedNotificationContact && this.props.editable && (
          <Pagination
            className="mx-auto d-block"
            currentPage={this.props.selectEmployeesPagination.pageNumber}
            totalPages={this.props.selectEmployeesPagination.totalPages}
            onChange={this.handleSearch}
          />
        )}
      </View>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => ({
  employeesSearchResult: hierarchySelectors.selectContactEmployees(state),
  selectEmployeesPagination: hierarchySelectors.selectContactEmployeesPagination(state),
  notificationContacts: hierarchySelectors.getNotificationContacts(state),
  contactFilter: getReduxForm(state)["contact-filter"],
});

const mapDispatchToProps: PropsFromDispatch = {
  setNotificationContact: hierarchyActionCreators.SetNotificationContact,
  callbacksEmployeeSearch: hierarchyActionCreators.CallbacksEmployeeSearch,
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  localize(),
  reduxForm({
    form: "contact-filter",
    initialValues: {
      lastName: "",
      employeeRefPrimary: "",
      dateOfBirth: "",
    },
    enableReinitialize: true,
  }),
)(Employees);
